import type { CandleArray, Timeframe } from '@shared/types/candle'
import { useQuery, keepPreviousData } from '@tanstack/react-query'

import { getCandles } from '@/services/api'

export interface OhlcvBar {
  time: number // Unix epoch seconds — Lightweight Charts UTCTimestamp, shifted to IST
  open: number
  high: number
  low: number
  close: number
  value?: number // volume
}

// All timestamps from the backend are UTC unix seconds.
// Shift by +5:30 so Lightweight Charts (which treats values as UTC) renders IST times.
export const IST_OFFSET = 19_800 // 5 * 3600 + 30 * 60

// Bars to fetch per timeframe.
// Target: 200-bar EMA warmup + ~300-500 visible bars = 500-700 minimum.
// Intraday needs more bars because each bar covers a short interval.
const FETCH_LIMIT: Record<Timeframe, number> = {
  '1m': 1_500, // ~4 NSE days / 3.5 US days visible after EMA200 warmup
  '3m': 1_500,
  '5m': 1_000, // ~13 trading days
  '15m': 800, // ~40 trading days
  '30m': 700, // ~44 trading days
  '1h': 700, // ~88 trading days
  '4h': 500, // ~500 trading days
  '1D': 750, // ~3 years
  '1W': 500, // ~10 years
  '1M': 240, // ~20 years
}

// How long cached candle data stays fresh before a background refetch triggers.
const STALE_MS: Record<Timeframe, number> = {
  '1m': 15_000,
  '3m': 20_000,
  '5m': 30_000,
  '15m': 60_000,
  '30m': 60_000,
  '1h': 60_000,
  '4h': 2 * 60_000,
  '1D': Infinity,
  '1W': Infinity,
  '1M': Infinity,
}

function columnarToBars(data: CandleArray): OhlcvBar[] {
  const bars: OhlcvBar[] = []
  for (let i = 0; i < data.t.length; i++) {
    bars.push({
      time: data.t[i] + IST_OFFSET,
      open: data.o[i],
      high: data.h[i],
      low: data.l[i],
      close: data.c[i],
      value: data.v[i],
    })
  }
  return bars
}

export function useCandles(ticker: string, timeframe: Timeframe) {
  return useQuery({
    queryKey: ['candles', ticker, timeframe],
    queryFn: async () => {
      const res = await getCandles(ticker, timeframe, undefined, undefined, FETCH_LIMIT[timeframe])
      return columnarToBars(res.data)
    },
    staleTime: STALE_MS[timeframe],
    gcTime: 30 * 60_000,
    enabled: Boolean(ticker),
    // Keep the previous timeframe's bars visible while the new fetch is in flight.
    placeholderData: keepPreviousData,
  })
}
